import * as React from 'react';

import type { HeadProps, PageProps } from 'gatsby';
import styled from 'styled-components';

import { Link } from 'stile-shared/src/components/2020/common/Link';
import { Prose, Section } from 'stile-shared/src/components/2020/common/Primitives';

import { PageLayout } from 'templates/2020/PageLayout';
import { SEO } from 'templates/2020/SEO';

export function Head(props: HeadProps) {
  return (
    <SEO
      title="CONASTA Competition | Terms & Conditions"
      description="Read our terms & conditions here!"
      slug={props.location.pathname}
    />
  );
}
const TermSection = styled(Section)`
  .prose ol > li::marker {
    font-weight: 600;
  }
  @media (max-width: 768px) {
    margin-top: 32px !important;
  }
  strong {
    font-weight: bold;
  }
  //   ol {
  //     list-style-type: lower-alpha;
  //     li > ol {
  //       list-style-type: lower-roman;
  //     }
  //   }
`;
function TermsPage(props: PageProps) {
  return (
    <PageLayout {...props}>
      <TermSection>
        <Prose>
          <h1>Stile Education “WIN a Trip to ConASTA 72” Competition – Terms &amp; Conditions</h1>

          <ol>
            <li>
              <h2>Promoter</h2>
              <p>
                The promoter of this competition is <strong>Stile Education Pty Ltd</strong>, ABN:
                98 159 359 681, located at 10/136 Exhibition St, Melbourne, Victoria, Australia.
              </p>
            </li>

            <li>
              <h2>Eligibility</h2>
              <ul>
                <li>
                  Entry is open to <strong>current Australian Secondary Schools</strong> only.
                </li>
                <li>
                  Entrants must be aged <strong>18 years or older</strong> at the time of entry.
                </li>
                <li>
                  Employees of Stile Education and their immediate families are not eligible to
                  enter.
                </li>
                <li>
                  <strong>One entry per person</strong> is permitted.
                </li>
              </ul>
            </li>

            <li>
              <h2>Competition Period</h2>
              <ul>
                <li>
                  The competition opens on <strong>Monday, 2 June 2025</strong>, and closes at{' '}
                  <strong>11:59 PM AEST on Monday, 16 June 2025</strong>.
                </li>
                <li>Entries submitted after this date will not be considered.</li>
              </ul>
            </li>

            <li>
              <h2>How to Enter</h2>
              <p>To enter, participants must:</p>
              <ol>
                <li>
                  <strong>Like</strong> the competition post on Facebook.
                </li>
                <li>
                  <strong>Follow</strong> @StileEducation on Facebook.
                </li>
                <li>
                  <strong>Complete the competition form</strong> found on the Facebook post,
                  answering the question:
                  <br />
                  <em>
                    "If you could take your students on a science excursion anywhere in the world,
                    where would you go—and why?"
                  </em>
                </li>
              </ol>
              <p>
                Incomplete entries or those that do not meet all three requirements will be deemed
                invalid.
              </p>
            </li>

            <li>
              <h2>Judging Criteria &amp; Winner Selection</h2>
              <ul>
                <li>
                  This is a <strong>game of skill</strong>. Chance plays no part in determining the
                  winner.
                </li>
                <li>
                  Entries will be judged on <strong>originality, creativity,</strong> and{' '}
                  <strong>insight </strong>
                  into what makes a science excursion meaningful and impactful.
                </li>
                <li>
                  The judging panel will consist of <strong>Stile Education staff members.</strong>
                </li>
                <li>
                  The winner will be selected and announced on{' '}
                  <strong>Wednesday, 18 June 2025</strong>.
                </li>
              </ul>
            </li>

            <li>
              <h2>Prize Details</h2>
              <p>The winner will receive:</p>
              <ul>
                <li>
                  One (1) <strong>full delegate pass to ConASTA 72</strong> in Perth (July 7–10,
                  2025).
                </li>
                <li>
                  <strong>Return economy flights</strong> from the winner’s nearest Australian
                  capital city to Perth.
                </li>
                <li>
                  <strong>Four (4) nights accommodation</strong> at the Pan Pacific Perth (Sunday, 6
                  July - Thursday, 10 July 2025).
                </li>
              </ul>
              <p>
                Total prize value is approximately <strong>AUD $3,000</strong>, depending on the
                winner’s location. <br />
                <br /> The prize is<strong> non-transferable</strong> and{' '}
                <strong>not redeemable for cash.</strong>
                If the winner is unable to attend, an alternate winner may be selected at Stile
                Education's discretion.
              </p>
            </li>

            <li>
              <h2>Notification</h2>
              <ul>
                <li>
                  The winner will be notified via{' '}
                  <strong> direct message on Facebook, phone call,</strong> and announced in a post
                  on the
                  <strong> @StileEducation Facebook page.</strong>
                </li>
                <li>
                  If the winner does not respond within <strong>72 hours,</strong> Stile Education
                  reserves the right to select another winner.
                </li>
              </ul>
            </li>

            <li>
              <h2>Facebook Disclaimer</h2>
              <ul>
                <li>
                  This promotion is in
                  <strong>
                    {' '}
                    no way sponsored, endorsed, administered by, or associated with Facebook.
                  </strong>
                </li>
                <li>
                  Participants agree to <strong>release Facebook</strong> from any responsibility
                  related to the promotion.
                </li>
              </ul>
            </li>

            <li>
              <h2>Privacy</h2>
              <ul>
                <li>
                  Personal information collected in connection with this promotion will be used in
                  accordance with Stile Education’s{' '}
                  <Link url="https://www.curriculumassociates.com/privacy/stile" openInNewTab>
                    Privacy Policy
                  </Link>
                  , and solely for the purposes of
                  administering the competition, contacting the winner, and marketing (if
                  consented).
                </li>
                <li>
                  All data will be handled in accordance with{' '}
                  <strong>Stile Education’s Privacy Policy.</strong>
                </li>
              </ul>
            </li>

            <li>
              <h2>General Conditions</h2>
              <ul>
                <li>
                  Stile Education reserves the right to <strong>verify the eligibility</strong> of
                  entries and participants.
                </li>
                <li>
                  The promoter may <strong>amend, suspend, or cancel</strong> the competition if
                  necessary, subject to regulatory approval.
                </li>
                <li>
                  By entering, participants agree to these <strong>Terms &amp; Conditions</strong>{' '}
                  and grant Stile Education permission to{' '}
                  <strong>publish their name and entry</strong> (in whole or part) on social media
                  and marketing channels.
                </li>
              </ul>
            </li>

            <li>
              <h2>Marketing Consent</h2>
              <p>
                By entering this competition, participants
                <strong> consent to being contacted by Stile Education</strong> for future marketing
                and promotional purposes, including updates on products, events, and educational
                resources. <br />
                <br />
                Participants may opt out of receiving marketing communications at any time by
                following the unsubscribe instructions included in those communications or by
                contacting Stile Education directly.
              </p>
            </li>
          </ol>
        </Prose>
      </TermSection>
    </PageLayout>
  );
}

export default TermsPage;
